// https://stackoverflow.com/questions/70426965/how-to-use-next-redux-wrapper-with-next-js-redux-toolkit-and-typescript-p
// https://stackblitz.com/edit/github-bizsur-azd1ro?file=src%2Fapp%2Fstore.ts,src%2Fapp%2Fhooks.ts

import { configureStore, Action, AnyAction, ThunkAction } from '@reduxjs/toolkit';
import { createWrapper, HYDRATE } from 'next-redux-wrapper';
import * as graphql from '~/services/graphql';
import combinedReducer from './combinedReducer';
import apolloConfig from '~/constants/apolloConfig';
import * as siteConfig from '~/constants/siteConfig';
import { createStateSyncMiddleware, initMessageListener } from 'redux-state-sync';

const reducer = (state: ReturnType<typeof combinedReducer>, action: AnyAction) => {
  if (action.type === HYDRATE) {
    const nextState = {
      ...state, // use previous state
      ...action.payload, // apply delta from hydration
    };
    return nextState;
  }

  return combinedReducer(state, action);
};

// This store instance is used in the storybook
export const store = configureStore({ reducer });

const actionsToSync = [
  'cart/getCartItemsAmount/fulfilled',
  'cart/getCart/fulfilled',
  'cart/updateItemQuantity/fulfilled',
  'cart/removeItem/fulfilled',
];

const makeStore = () => {
  const siteStore = configureStore({
    reducer,
    middleware: getDefaultMiddleware => {
      const defaultMiddleware = getDefaultMiddleware({
        thunk: {
          extraArgument: {
            // pass "apolloConfig" and "siteConfig" data to the "extraArgument" to use within the thunk graphql requests
            // https://redux-toolkit.js.org/api/getDefaultMiddleware#customizing-the-included-middleware
            apolloConfig,
            siteConfig,
            graphql,
          },
        },
      });

      if (typeof window !== 'undefined') {
        return defaultMiddleware.concat(
          createStateSyncMiddleware({
            whitelist: actionsToSync,
          }),
        );
      }

      return defaultMiddleware;
    },
  });
  initMessageListener(siteStore);

  return siteStore;
};

export const wrapper = createWrapper(makeStore, {
  // debug: true
});

export type TStore = ReturnType<typeof makeStore>;

type TError = {
  error?: string;
};

export type AppDispatch = TStore['dispatch'] & TError;
export type RootState = ReturnType<TStore['getState']>;
export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  unknown,
  Action<string>
>;
